import { Image, X } from '@phosphor-icons/react';
import type { PublicUser } from '@sirocco/shared';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { messages, users as usersApi } from '../../api/endpoints';
import { ScreenError, ScreenLoader, SubScreen } from '../../components/layout/Screens';
import { Avatar } from '../../components/ui/Avatar';
import { EmptyState } from '../../components/ui/Bits';
import { Button } from '../../components/ui/Button';
import { CheckMark, Field, SearchField, TextArea } from '../../components/ui/Field';
import { PhotoPicker } from '../../components/ui/PhotoPicker';
import { useAsync, useDebounced } from '../../hooks/useAsync';
import { t } from '../../i18n';

/**
 * Creation d'un groupe.
 *
 * Les membres deja choisis restent visibles en pastilles au-dessus de la
 * liste : on garde son equipe sous les yeux pendant qu'on cherche.
 */
export function NewGroupScreen() {
  const navigate = useNavigate();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [search, setSearch] = useState('');
  const [photo, setPhoto] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  /*
    On retient les profils choisis, pas seulement leurs identifiants : les
    pastilles du haut doivent survivre a un changement de recherche, qui
    remplace entierement la liste du dessous.
  */
  const [chosen, setChosen] = useState<PublicUser[]>([]);
  const selected = chosen.map((member) => member.id);

  // Sans recherche, on propose ses amis ; des qu'on tape, on cherche dans
  // tout l'annuaire : un groupe n'est pas reserve a ses amis.
  const debounced = useDebounced(search);
  const { data, loading, error, reload } = useAsync(
    /* Un groupe se compose d'amis : voir `AddMembersScreen`, meme raison. */
    () => usersApi.friendList(),
    [debounced],
  );
  /* Meme filtre local que dans `AddMembersScreen`, et meme raison. */
  const listed = (data ?? []).filter((ami) =>
    ami.username.toLowerCase().includes(debounced.trim().toLowerCase()),
  );
  const searching = debounced.trim().length > 0;

  function toggle(member: PublicUser) {
    setChosen((current) =>
      current.some((entry) => entry.id === member.id)
        ? current.filter((entry) => entry.id !== member.id)
        : [...current, member],
    );
  }

  async function create() {
    setPending(true);
    setFailure(null);
    try {
      const conversation = await messages.createGroup(
        title.trim(),
        description.trim(),
        selected,
        photo,
      );
      // On entre directement dans le groupe : revenir ici n'aurait pas de sens.
      navigate(`/app/messages/${conversation.id}`, { replace: true });
    } catch (cause) {
      setFailure(cause instanceof Error ? cause.message : t.messages.nouveauGroupe.impossible);
    } finally {
      setPending(false);
    }
  }

  return (
    <SubScreen
      title={t.messages.nouveauGroupe.titre}
      bottomBar={
        <Button
          block
          loading={pending}
          disabled={title.trim().length === 0 || selected.length === 0}
          onClick={() => void create()}
        >
          {t.messages.nouveauGroupe.creer}
        </Button>
      }
    >
      {loading && <ScreenLoader />}
      {error && <ScreenError message={error} onRetry={reload} />}

      {data && (
        <>
          <div className="ms-group-top">
            <PhotoPicker
              preset="avatar"
              label={t.messages.nouveauGroupe.choisirPhoto}
              className="ms-group-photo"
              onPicked={setPhoto}
              onError={setFailure}
            >
              {photo ? <img src={photo} alt="" /> : <Image size={28} />}
            </PhotoPicker>

            <Field
              tone="spot"
              label={t.messages.nouveauGroupe.nom}
              className="ms-group-top__field"
              placeholder={t.messages.nouveauGroupe.nomExemple}
              value={title}
              onChange={(event) => setTitle(event.target.value)}
            />
          </div>

          {failure && <p className="field__error">{failure}</p>}

          <TextArea
            tone="spot"
            label={t.messages.nouveauGroupe.description}
            placeholder={t.messages.nouveauGroupe.descriptionExemple}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />

          <div className="ms-members-head">
            <h2 className="ms-members-head__title">{t.messages.commun.membres}</h2>
            <span className="ms-members-head__count">
              {t.messages.nouveauGroupe.selectionnes(selected.length)}
            </span>
          </div>

          {chosen.length > 0 && (
            <div className="ms-chips">
              {chosen.map((contact) => (
                <span key={contact.id} className="ms-chip">
                  <Avatar user={contact} size={22} />
                  {contact.username}
                  <button
                    className="ms-chip__remove"
                    aria-label={t.messages.commun.retirerQuelquun(contact.username)}
                    onClick={() => toggle(contact)}
                  >
                    <X size={16} weight="bold" />
                  </button>
                </span>
              ))}
            </div>
          )}

          <SearchField
            tone="spot"
            placeholder={t.messages.nouveauGroupe.chercher}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />

          {searching && listed.length === 0 && (
            <EmptyState title={t.messages.commun.aucunMembre} hint={t.messages.commun.verifie} />
          )}

          {!searching && listed.length === 0 && (
            <EmptyState
              title={t.messages.commun.aucunAmi}
              hint={t.messages.commun.chercheUnPseudo}
            />
          )}

          <div className="ms-contacts">
            {listed.map((contact) => {
              const coche = selected.includes(contact.id);
              return (
                /* C'est la ligne entiere qui coche, pas la case au bout :
                   viser vingt-six pixels a chaque nom n'est pas un geste
                   qu'on fait au pouce sur une longue liste. */
                <button
                  key={contact.id}
                  type="button"
                  role="checkbox"
                  aria-checked={coche}
                  className="ms-contact ms-contact--pick"
                  onClick={() => toggle(contact)}
                >
                  <Avatar user={contact} size={38} />
                  <span className="ms-contact__name">{contact.username}</span>
                  <CheckMark checked={coche} />
                </button>
              );
            })}
          </div>
        </>
      )}
    </SubScreen>
  );
}
